import React, { useState, useEffect } from 'react';
import {
    Check, X, Zap, Shield, Layout, Loader2, AlertTriangle, ArrowLeft, ChevronRight, Globe
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import toast from 'react-hot-toast';
import SEO from '../../components/shared/SEO';
import { authAPI, billingAPI } from '../../services/api'; // Added billingAPI

const SubscriptionPage = () => {
    const { user, setUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'
    const location = useLocation(); // Hook to read the URL

    // Check if we were redirected here due to a 402 Payment Required error
    const queryParams = new URLSearchParams(location.search);
    const isExpiredRedirect = queryParams.get('expired') === 'true';
    // Fetch latest user data on mount
    const fetchData = async () => {
        try {
            setLoading(true)
            const data = await authAPI.getMe();
            if (data) setUser(data)
        } catch (error) {
            console.log(error);
        }
        finally {
            setLoading(false)
        }
    };

    useEffect(() => {
        fetchData();
        if (isExpiredRedirect) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [isExpiredRedirect]);

    const isPro = user?.tier === 'pro';

    // --- Dynamic Pricing Variables ---
    // const planId = billingCycle === 'monthly' ? '1335270' : '1335259';
    const planId = billingCycle === 'monthly' ? '1352008' : '1352007';

    const priceDisplay = billingCycle === 'monthly' ? '$5' : '$50';
    const periodDisplay = billingCycle === 'monthly' ? '/mo' : '/year';

    const formattedDate = user?.nextBillDate
        ? new Date(user.nextBillDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
        : '';

    // --- Handlers ---

    const handleCheckout = async () => {
        setLoading(true);
        try {
            // Using the service API pattern
            // Note: We pass the planId so the backend knows which Stripe price to use
            const data = await billingAPI.createCheckoutSession({
                plan: planId,
                userId: user._id,
                email: user.email
            });

            if (data?.url) {
                window.location.href = data.url;
            } else {
                console.error("Could not start checkout session.");
            }
        } catch (error) {
            console.error("Checkout Error:", error);
            console.error(error.response?.data?.message || "Checkout failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleManage = async () => {
        setLoading(true);
        try {
            const data = await billingAPI.createPortalSession({
                userId: user._id,
            });

            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Could not open billing portal.");
            }
        } catch (error) {
            console.error("Portal Error:", error);
            toast.error("Failed to connect to billing server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SEO title="Settings | EnvDock" description="Manage your account." noIndex={true} />

            <div className="max-w-6xl mx-auto space-y-10">

                {/* --- 402 EXPIRED BANNER --- */}
                {isExpiredRedirect && (
                    <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-4 animate-in fade-in slide-in-from-top-4">
                        <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center text-red-500 shrink-0">
                            <AlertTriangle size={24} />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-red-500 font-bold text-lg">Action Required: API Access Suspended</h3>
                            <p className="text-gray-400 text-sm mt-1">Your subscription has expired or a payment failed. Please update your billing information to restore full API functionality.</p>
                        </div>
                        <Button onClick={handleManage} className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/20 whitespace-nowrap">
                            Update Payment Info
                        </Button>
                    </div>
                )}

                {/* Page Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                    <div>
                        <h1 className="text-4xl font-black text-white tracking-tighter">
                            Subscription & Billing
                        </h1>
                        <p className="text-gray-500 font-medium mt-1">
                            Manage your plan, billing details, and invoices.
                        </p>
                    </div>

                    {/* Current Status Badge & Auto-Renew Info */}
                    <div className="flex flex-col items-end gap-2">
                       {!loading && <div className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-full px-4 py-2">
                            <span className="text-sm text-gray-400">Current Plan:</span>
                            <span className={`text-sm font-bold uppercase tracking-wide ${isPro ? 'text-orange-500' : 'text-white'}`}>
                                {isPro ? 'PRO PLAN' : 'FREE TIER'}
                            </span>
                        </div>}

                        {/* Auto-Renew Status Display */}
                        {!loading && isPro && user?.subscriptionStatus && (
                            <div className="text-[11px] font-mono tracking-wider uppercase text-right pr-2">
                                {user.subscriptionStatus === 'active' && (
                                    <span className="text-emerald-500">Renews on {formattedDate}</span>
                                )}
                                {user.subscriptionStatus === 'on_trial' && (
                                    <span className="text-emerald-500">Trial ends on {formattedDate}</span>
                                )}
                                {user.subscriptionStatus === 'cancelled' && (
                                    <span className="text-orange-500">Auto-renew OFF. Ends {formattedDate}</span>
                                )}
                                {user.subscriptionStatus === 'past_due' && (
                                    <span className="text-red-500 font-bold">Payment Failed. Update billing.</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* --- TOGGLE SWITCH --- */}
                {!loading && !isPro && (
                    <div className="flex justify-center mb-8">
                        <div className="bg-[#111] p-1 rounded-xl border border-white/10 flex relative">
                            <button
                                onClick={() => setBillingCycle('monthly')}
                                className={`px-6 py-2 text-sm font-bold rounded-lg transition-all ${billingCycle === 'monthly' ? 'bg-gray-800 text-white shadow-lg' : 'text-gray-500 hover:text-white'
                                    }`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingCycle('yearly')}
                                className={`px-6 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-orange-600 text-white shadow-lg' : 'text-gray-500 hover:text-white'
                                    }`}
                            >
                                Yearly
                            </button>
                        </div>
                    </div>
                )}

                {/* Pricing Grid */}
                <div className="grid lg:grid-cols-2 gap-8 items-start">

                    {/* --- FREE PLAN --- */}
                    <div className={`
                        relative p-8 rounded-3xl border transition-all duration-300 flex flex-col h-full
                        bg-[#090909]
                        ${isPro ? 'border-white/5 opacity-60' : 'border-white/10 shadow-lg'}
                    `}>
                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center text-white">
                                    <Layout size={20} />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Developer</h3>
                                    <p className="text-xs text-gray-500 uppercase tracking-widest">Free Forever</p>
                                </div>
                            </div>

                            <div className="mb-8">
                                <span className="text-4xl font-black text-white tracking-tighter">$0</span>
                                <span className="text-gray-500">/mo</span>
                            </div>

                            {/* FREE TIER FEATURES */}
                            <ul className="space-y-4 mb-8 border-t border-white/5 pt-8">
                                <FeatureItem text="2 Projects" active={true} />
                                <FeatureItem text="1 Team Member / Project" active={true} />
                                <FeatureItem text="Unlimited Secrets" active={true} />
                                <FeatureItem text="Unlimited Rollbacks" active={true} />
                                <FeatureItem text="CLI Access" active={true} />
                                <FeatureItem text="Integrations" active={true} />
                                <FeatureItem text="3 Day Audit History" active={true} />

                                {/* Disabled Features */}
                                <FeatureItem text="1 Service Token / Project" active={true} />
                            </ul>
                        </div>

                        <div className="mt-auto">
                            <Button
                                variant="outline"
                                className="w-full border-white/10 text-gray-400 hover:bg-white/5"
                                disabled={true}
                            >
                                {isPro ? "Downgrade" : "Current Plan"}
                            </Button>
                        </div>
                    </div>

                    {/* --- PRO PLAN --- */}
                    <div className={`
                        relative p-8 rounded-3xl border-2 transition-all duration-300 overflow-hidden group flex flex-col h-full
                        ${isPro
                            ? 'bg-[#090909] border-orange-500/50 shadow-[0_0_40px_-10px_rgba(249,115,22,0.15)]'
                            : 'bg-gradient-to-b from-orange-500/5 to-[#090909] border-orange-500 shadow-[0_0_40px_-10px_rgba(249,115,22,0.3)]'
                        }
                    `}>
                        {/* "Popular" Badge */}
                        {!isPro && (
                            <div className="absolute top-0 right-0">
                                <div className="bg-orange-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-bl-2xl">
                                    Recommended
                                </div>
                            </div>
                        )}

                        <div>
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-black shadow-lg shadow-orange-500/20">
                                    <Zap size={20} fill="currentColor" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-white">Pro Team</h3>
                                    <p className="text-xs text-orange-400 uppercase tracking-widest">
                                        {billingCycle === 'yearly' ? 'Best Value' : 'Power User'}
                                    </p>
                                </div>
                            </div>

                            {/* Price Display */}
                            <div className="mb-8">
                                <div className="flex items-baseline gap-1">
                                    <span className="text-4xl font-black text-white tracking-tighter">{priceDisplay}</span>
                                    <span className="text-gray-500">{periodDisplay}</span>
                                </div>
                            </div>

                            {/* PRO TIER FEATURES */}
                            <ul className="space-y-4 mb-8 border-t border-orange-500/20 pt-8">
                                <FeatureItem text="Up to 30 Projects" active={true} highlight />
                                <FeatureItem text="12 Team Members / Project" active={true} highlight />
                                {/* Pro Exclusives */}
                                <FeatureItem text="5 Service Tokens (CI/CD) / Project" active={true} highlight />
                                <FeatureItem text="Full Audit Logs" active={true} highlight />
                                <FeatureItem text="Unlimited Secrets" active={true} />
                                <FeatureItem text="Unlimited Rollbacks" active={true} />
                                <FeatureItem text="CLI Access" active={true} />
                                <FeatureItem text="Cloud Integrations" active={true} />

                            </ul>
                        </div>

                        <div className="mt-auto">
                            <Button
                                onClick={isPro ? handleManage : handleCheckout}
                                disabled={loading}
                                className={`w-full py-4 text-sm font-bold tracking-wide uppercase shadow-lg transition-all ${isPro
                                    ? 'bg-[#1a1a1a] hover:bg-[#222] text-white border border-white/10'
                                    : 'bg-orange-500 hover:bg-orange-400 text-black shadow-orange-500/20 hover:shadow-orange-500/40'
                                    }`}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <Loader2 size={16} className="animate-spin" /> Processing...
                                    </div>
                                ) : isPro ? (
                                    "Manage Subscription"
                                ) : (
                                    `Upgrade ${billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}`
                                )}
                            </Button>

                            {/* Updated Payment Gateway Text */}
                            <p className="text-center text-[10px] text-gray-500 mt-4 flex items-center justify-center gap-1">
                                <Shield size={10} /> Secure payment via Lemon Squeezy
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-16 p-8 rounded-[2.5rem] bg-[#0d0d0d] border border-white/5 relative overflow-hidden group">
                    {/* Decorative Background Glow */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[100px] pointer-events-none group-hover:bg-blue-500/10 transition-colors duration-700" />

                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex items-center flex-col md:flex-row  gap-6">
                            <div className="w-14 h-14 rounded-2xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-gray-400 group-hover:text-white transition-colors">
                                <Globe size={28} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h4 className="text-xl font-black text-white tracking-tight text-center md:text-left uppercase">
                                    Need more than <span className="text-blue-500">Pro?</span>
                                </h4>
                                <p className="text-sm text-gray-500 max-w-md mt-1 text-center md:text-left">
                                    For large engineering teams, custom limits, or specialized security requirements, our enterprise-ready infrastructure scales with you.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col items-center md:items-end gap-3 w-full sm:w-auto">
                            <a
                                href="mailto:envdock@gmail.com"
                                className="w-full sm:w-auto px-8 py-3 bg-white text-black rounded-xl flex justify-center font-black text-xs uppercase tracking-widest hover:bg-blue-500 hover:text-white transition-all shadow-xl items-center gap-2 group/btn"
                            >
                                Contact Sales
                                <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                            </a>
                            <p className="text-[10px] text-gray-700 font-bold uppercase tracking-tighter">
                                Response time: <span className="text-gray-500">&lt; 12 hours</span>
                            </p>
                        </div>
                    </div>
                </div>

            </div>
        </>
    );
};

// Helper Component for Feature List
const FeatureItem = ({ text, active = true, highlight = false }) => (
    <li className={`flex items-start gap-3 text-sm ${!active ? 'opacity-40' : ''}`}>
        <div className={`mt-0.5 shrink-0 ${highlight ? 'text-orange-500' : 'text-gray-500'}`}>
            {active ? <Check size={16} strokeWidth={3} /> : <X size={16} />}
        </div>
        <span className={`${highlight ? 'text-white font-medium' : 'text-gray-400'}`}>
            {text}
        </span>
    </li>
);

export default SubscriptionPage;